// src/pages/demo/DemoContentLibrary.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { contentLibraryAPI } from '../../services/api';

const BRAND = {
    teal: '#75B9BE',
    charcoal: '#2D4042',
    coral: '#F28482',
    cream: '#FDF6EC',
    grey: '#F0F2F5',
    danger: '#E07A5F',
};

const KS_LABEL = { ks1: 'KS1', ks2: 'KS2', ks3: 'KS3', ks4: 'KS4' };

const SUBJECT_COLOURS = {
    Geography:  { bg: '#EFF6FF', border: '#93C5FD', text: '#1D4ED8', active: '#3B82F6' },
    History:    { bg: '#FFF7ED', border: '#FDB98A', text: '#C2410C', active: '#F97316' },
    English:    { bg: '#F5F3FF', border: '#C4B5FD', text: '#6D28D9', active: '#8B5CF6' },
};
const DEFAULT_COLOUR = { bg: '#F0FDFA', border: '#75B9BE', text: '#0F766E', active: '#75B9BE' };

// ── Filter Chip ───────────────────────────────────────────────────────────────
const Chip = ({ label, active, onClick, colour }) => (
    <button onClick={onClick} style={{
        padding: '6px 14px', borderRadius: '999px', fontWeight: '700', fontSize: '0.85em', cursor: 'pointer', transition: 'all 0.15s',
        border: active ? `2px solid ${colour.active}` : '2px solid #E5E7EB',
        background: active ? colour.bg : 'white',
        color: active ? colour.text : '#6B7280',
    }}>
        {label}
    </button>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const DemoContentLibrary = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const ns = location.pathname.startsWith('/lite/') ? '/lite' : location.pathname.startsWith('/demo/') ? '/demo' : '';
    const [lessons, setLessons] = useState([]);
    const [meta, setMeta] = useState({ subjects: [], yearGroups: [], keyStages: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // JCQ mode: navigated here from the Extra Time Test builder
    const jcqState = location.state || {};
    const isJcqMode = Boolean(jcqState.jcqMode);

    // Fresh start when coming from dashboard or JCQ; restore filters when navigating back from a lesson view
    const fromDashboard = location.state?.fresh === true;
    const _savedFilters = (() => {
        if (fromDashboard || isJcqMode) return {};
        try { return JSON.parse(sessionStorage.getItem('clFilters') || '{}'); } catch { return {}; }
    })();
    const [filterSubject, setFilterSubject]     = useState(_savedFilters.subject    || '');
    const [filterKeyStage, setFilterKeyStage]   = useState(_savedFilters.keyStage   || '');
    const [filterYearGroup, setFilterYearGroup] = useState(isJcqMode ? (jcqState.yearGroup || 'Year 11') : (_savedFilters.yearGroup  || ''));
    const [filterUnit, setFilterUnit]           = useState(_savedFilters.unit       || '');
    const [filterSearch, setFilterSearch]       = useState(_savedFilters.search     || '');

    // Clear stale saved filters and remove the fresh flag so browser-back doesn't re-trigger
    useEffect(() => {
        if (fromDashboard) {
            sessionStorage.removeItem('clFilters');
            const stateWithoutFresh = { ...(location.state || {}) };
            delete stateWithoutFresh.fresh;
            navigate(location.pathname, {
                replace: true,
                state: Object.keys(stateWithoutFresh).length ? stateWithoutFresh : null,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        Promise.all([
            contentLibraryAPI.list(),
            contentLibraryAPI.getMeta(),
        ]).then(([l, m]) => {
            // Expand multi-chunk lessons into separate entries (Part 1, Part 2…)
            const expanded = (l.data || []).flatMap(lesson => {
                const count = lesson.slideshowCount ?? (lesson.slideshows || []).length;
                if (count <= 1) return [{ ...lesson, _viewKey: String(lesson._id) }];
                return Array.from({ length: count }, (_, i) => ({
                    ...lesson,
                    lessonTitle: `${lesson.lessonTitle} (Part ${i + 1})`,
                    _partIndex: i,
                    _partTotal: count,
                    _viewKey: `${lesson._id}:part:${i}`,
                }));
            });
            setLessons(expanded);
            setMeta(m.data || {});
        }).catch(() => setError('Failed to load content library.'))
          .finally(() => setLoading(false));
    }, []);

    // Reset dependent filters when parent changes
    const handleSubjectChange = (val) => { setFilterSubject(val); setFilterKeyStage(''); setFilterYearGroup(''); setFilterUnit(''); };
    const handleKSChange      = (val) => { setFilterKeyStage(val); setFilterYearGroup(''); setFilterUnit(''); };
    const handleYearChange    = (val) => { setFilterYearGroup(val); setFilterUnit(''); };

    // Derived available options given current selections
    const availableKS = useMemo(() => {
        const ks = new Set(lessons.filter(l => !filterSubject || l.platformSubject === filterSubject).map(l => l.keyStage));
        return [...ks].sort();
    }, [lessons, filterSubject]);

    const availableYears = useMemo(() => {
        const yrs = new Set(lessons.filter(l =>
            (!filterSubject || l.platformSubject === filterSubject) &&
            (!filterKeyStage || l.keyStage === filterKeyStage)
        ).map(l => l.yearGroup));
        const num = s => parseInt(s.replace(/\D/g, '')) || 0;
        return [...yrs].sort((a, b) => num(a) - num(b));
    }, [lessons, filterSubject, filterKeyStage]);

    const availableUnits = useMemo(() => {
        const units = new Set(lessons.filter(l =>
            (!filterSubject   || l.platformSubject === filterSubject) &&
            (!filterKeyStage  || l.keyStage        === filterKeyStage) &&
            (!filterYearGroup || l.yearGroup       === filterYearGroup)
        ).map(l => l.unitTitle).filter(Boolean));
        return [...units].sort();
    }, [lessons, filterSubject, filterKeyStage, filterYearGroup]);

    const filtered = useMemo(() => lessons.filter(l => {
        if (filterSubject   && l.platformSubject !== filterSubject)  return false;
        if (filterKeyStage  && l.keyStage        !== filterKeyStage) return false;
        if (filterYearGroup && l.yearGroup       !== filterYearGroup) return false;
        if (filterUnit      && l.unitTitle       !== filterUnit)      return false;
        if (filterSearch) {
            const q = filterSearch.toLowerCase();
            const inTitle = l.lessonTitle.toLowerCase().includes(q);
            const inUnit  = (l.unitTitle || '').toLowerCase().includes(q);
            const keywordTerms = l.keywordTerms || (l.keywords || []).map(kw => kw.term);
            const inKw    = keywordTerms.some(term => (term || '').toLowerCase().includes(q));
            if (!inTitle && !inUnit && !inKw) return false;
        }
        return true;
    }), [lessons, filterSubject, filterKeyStage, filterYearGroup, filterUnit, filterSearch]);

    // Group by unit (then subject+ks+year as fallback)
    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach(l => {
            const key = l.unitTitle || `${l.platformSubject} ${l.keyStage} ${l.yearGroup}`;
            if (!map[key]) map[key] = { unitTitle: l.unitTitle || '', subject: l.platformSubject, keyStage: l.keyStage, yearGroup: l.yearGroup, lessons: [] };
            map[key].lessons.push(l);
        });
        return Object.values(map).sort((a, b) => {
            if (a.subject !== b.subject) return a.subject.localeCompare(b.subject);
            if (a.keyStage !== b.keyStage) return a.keyStage.localeCompare(b.keyStage);
            return parseInt(a.yearGroup) - parseInt(b.yearGroup);
        });
    }, [filtered]);

    const handleOpen = (lesson) => {
        if (!isJcqMode) {
            sessionStorage.setItem('clFilters', JSON.stringify({
                subject: filterSubject, keyStage: filterKeyStage,
                yearGroup: filterYearGroup, unit: filterUnit, search: filterSearch,
            }));
        }
        const url = lesson._partIndex !== undefined
            ? `${ns}/content-library/${lesson._id}/view?part=${lesson._partIndex}`
            : `${ns}/content-library/${lesson._id}/view`;
        navigate(url, {
            state: isJcqMode ? {
                ...jcqState,
                jcqLessonMeta: {
                    platformSubject: lesson.platformSubject,
                    yearGroup: lesson.yearGroup,
                    unitTitle: lesson.unitTitle || lesson.lessonTitle,
                }
            } : undefined
        });
    };

    const subjectColour = (s) => SUBJECT_COLOURS[s] || DEFAULT_COLOUR;

    return (
        <div style={{ minHeight: '100vh', background: BRAND.cream, fontFamily: 'Inter, sans-serif' }}>
            {/* Nav */}
            <div style={{ background: 'white', borderBottom: '2px solid #E5E7EB', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '60px', position: 'sticky', top: 0, zIndex: 10 }}>
                {isJcqMode
                    ? <button onClick={() => navigate(jcqState.jcqReturnPath || '/jcq/extra-time-test')} style={{ background: 'none', border: 'none', color: '#5D7375', cursor: 'pointer', fontSize: '0.88em', fontWeight: '600', padding: 0 }}>← Back to Test Builder</button>
                    : <Link to={`${ns}/dashboard`} style={{ fontSize: '0.88em', fontWeight: '600', color: '#5D7375', textDecoration: 'none' }}>← Dashboard</Link>
                }
                <button onClick={onLogout} style={{ background: 'white', color: BRAND.charcoal, border: `2px solid ${BRAND.charcoal}`, borderBottom: `4px solid ${BRAND.charcoal}`, borderRadius: '10px', padding: '6px 16px', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}>Log Out</button>
            </div>

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '1.8rem' }}>📚</span>
                            <h1 style={{ margin: 0, fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.9rem', color: BRAND.charcoal }}>Content Library</h1>
                            <span style={{ background: BRAND.teal, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.78em', padding: '3px 10px', borderRadius: '999px' }}>
                                {lessons.length} lessons
                            </span>
                        </div>
                        <p style={{ margin: 0, color: '#5D7375', fontSize: '0.9em' }}>Pre-built Oak Academy lessons — ready to use in your class instantly. No generation needed.</p>
                    </div>
                    <img src={require('../classlabs.png')} alt="ClassLabs" style={{ height: '72px', display: 'block' }} />
                </div>

                {/* JCQ mode banner */}
                {isJcqMode && (
                    <div style={{ background: '#FFF8E7', border: '2px solid #F59E0B', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px 16px' }}>
                        <span style={{ fontSize: '1.2rem' }}>📋</span>
                        <div style={{ flex: 1 }}>
                            <strong style={{ color: BRAND.charcoal, fontSize: '0.9em' }}>JCQ Extra Time Test — Content Selection</strong>
                            <p style={{ color: '#78716C', fontSize: '0.82em', margin: '2px 0 0' }}>
                                Showing Year 11 lessons across all subjects. View a lesson then click <strong>Add to Extra Time Test</strong>.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate(jcqState.jcqReturnPath || '/jcq/extra-time-test')}
                            style={{ background: 'none', border: '2px solid #F59E0B', borderRadius: '8px', color: '#92400E', cursor: 'pointer', fontWeight: '700', fontSize: '0.82em', padding: '6px 12px', whiteSpace: 'nowrap' }}
                        >
                            ← Back to test builder
                        </button>
                    </div>
                )}

                {/* Filter panel */}
                <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '18px 20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                    {/* Row 1: Subject */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9CA3AF', minWidth: '60px' }}>Subject</span>
                        <Chip label="All" active={!filterSubject} onClick={() => handleSubjectChange('')} colour={{ bg: '#F0F2F5', border: '#E5E7EB', text: BRAND.charcoal, active: BRAND.charcoal }} />
                        {meta.subjects.sort().map(s => (
                            <Chip key={s} label={s} active={filterSubject === s} onClick={() => handleSubjectChange(filterSubject === s ? '' : s)} colour={subjectColour(s)} />
                        ))}
                    </div>

                    {/* Row 2: Key Stage */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9CA3AF', minWidth: '60px' }}>Key Stage</span>
                        <Chip label="All" active={!filterKeyStage} onClick={() => handleKSChange('')} colour={{ bg: '#F0F2F5', border: '#E5E7EB', text: BRAND.charcoal, active: BRAND.charcoal }} />
                        {availableKS.map(ks => (
                            <Chip key={ks} label={KS_LABEL[ks] || ks} active={filterKeyStage === ks} onClick={() => handleKSChange(filterKeyStage === ks ? '' : ks)} colour={{ bg: '#F0FDFA', border: BRAND.teal, text: '#0F766E', active: BRAND.teal }} />
                        ))}
                    </div>

                    {/* Row 3: Year Group */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9CA3AF', minWidth: '60px' }}>Year</span>
                        <Chip label="All" active={!filterYearGroup} onClick={() => handleYearChange('')} colour={{ bg: '#F0F2F5', border: '#E5E7EB', text: BRAND.charcoal, active: BRAND.charcoal }} />
                        {availableYears.map(y => (
                            <Chip key={y} label={y.replace('Year ', 'Y')} active={filterYearGroup === y} onClick={() => handleYearChange(filterYearGroup === y ? '' : y)} colour={{ bg: '#F0FDFA', border: BRAND.teal, text: '#0F766E', active: BRAND.teal }} />
                        ))}
                    </div>

                    {/* Row 4: Unit */}
                    {availableUnits.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontWeight: '800', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9CA3AF', minWidth: '60px' }}>Unit</span>
                            <select
                                value={filterUnit}
                                onChange={e => setFilterUnit(e.target.value)}
                                style={{ padding: '7px 12px', borderRadius: '8px', border: '2px solid #E5E7EB', fontSize: '0.88em', fontWeight: '600', color: filterUnit ? BRAND.charcoal : '#9CA3AF', outline: 'none', maxWidth: '400px', cursor: 'pointer' }}
                            >
                                <option value="">All units</option>
                                {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Row 5: Search + count */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input
                            placeholder="Search lessons or units..."
                            value={filterSearch}
                            onChange={e => setFilterSearch(e.target.value)}
                            style={{ flex: 1, padding: '9px 14px', borderRadius: '8px', border: '2px solid #E5E7EB', fontSize: '0.9em', fontWeight: '500', color: BRAND.charcoal, outline: 'none', maxWidth: '400px' }}
                        />
                        <span style={{ fontSize: '0.85em', color: '#9CA3AF', fontWeight: '600' }}>
                            {filtered.length} lesson{filtered.length !== 1 ? 's' : ''}
                            {(filterSubject || filterKeyStage || filterYearGroup || filterUnit || filterSearch) && (
                                <button onClick={() => { setFilterSubject(''); setFilterKeyStage(''); setFilterYearGroup(''); setFilterUnit(''); setFilterSearch(''); }}
                                    style={{ marginLeft: '10px', background: 'none', border: 'none', color: BRAND.teal, fontWeight: '700', cursor: 'pointer', fontSize: '0.9em', padding: 0 }}>
                                    Clear filters ×
                                </button>
                            )}
                        </span>
                    </div>
                </div>

                {error && <p style={{ color: BRAND.danger, fontWeight: '700' }}>{error}</p>}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#5D7375' }}>Loading...</div>
                ) : grouped.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#5D7375' }}>No lessons match your filters.</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                        {grouped.map(group => {
                            const col = subjectColour(group.subject);
                            return (
                                <div key={`${group.subject}-${group.unitTitle}-${group.yearGroup}`}
                                    style={{ background: 'white', borderRadius: '14px', border: `1.5px solid ${col.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                                    {/* Card header */}
                                    <div style={{ background: col.bg, padding: '14px 16px', borderBottom: `1.5px solid ${col.border}` }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                            <span style={{ background: 'white', border: `1.5px solid ${col.border}`, color: col.text, fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.7em', padding: '2px 9px', borderRadius: '999px' }}>
                                                {group.subject}
                                            </span>
                                            <span style={{ background: 'white', border: `1.5px solid ${BRAND.teal}`, color: '#0F766E', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.7em', padding: '2px 9px', borderRadius: '999px' }}>
                                                {KS_LABEL[group.keyStage] || group.keyStage} · {group.yearGroup}
                                            </span>
                                            <span style={{ marginLeft: 'auto', fontSize: '0.75em', color: col.text, fontWeight: '700' }}>
                                                {group.lessons.length} lesson{group.lessons.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1rem', color: BRAND.charcoal, lineHeight: 1.3 }}>
                                            {group.unitTitle || `${group.subject} ${group.yearGroup}`}
                                        </div>
                                    </div>


                                    {/* Lesson list */}
                                    <div style={{ flex: 1, padding: '10px 0' }}>
                                        {group.lessons.map((lesson, idx) => (
                                            <div key={lesson._viewKey || lesson._id} style={{
                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                padding: '8px 16px',
                                                borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none',
                                            }}>
                                                <span style={{ minWidth: '20px', textAlign: 'right', fontSize: '0.72em', fontWeight: '700', color: '#9CA3AF' }}>{idx + 1}</span>
                                                <span style={{ flex: 1, fontSize: '0.85em', fontWeight: '600', color: BRAND.charcoal, lineHeight: 1.35 }}>{lesson.lessonTitle}</span>
                                                <button
                                                    onClick={() => handleOpen(lesson)}
                                                    style={{ flexShrink: 0, padding: '5px 11px', borderRadius: '7px', border: 'none', background: BRAND.teal, color: 'white', fontWeight: '800', fontSize: '0.75em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                    View →
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

        </div>
    );
};

export default DemoContentLibrary;
